import { PublicAccessCarChassisSectionDto } from '../types/models.types';
import { getRatingMessage } from '@/helpers/ratingState';
import { mapScoreToNumber, mapEquipmentStatusToBadge, mapChassisStatusToBadge } from './mapper-helpers';
import { RatingSection } from './mapper-types';
import { InspectionItem } from '@/components/InspectionList/InspectionList.types';

export interface MappedChassisSection {
  ratingSection: RatingSection;
  remarks: string[];
  chassisItems: InspectionItem[];
  expertSummary: string | null;
}

export const mapChassisSectionToUI = (
  section: PublicAccessCarChassisSectionDto | null | undefined,
): MappedChassisSection | null => {
  if (!section) {
    return null;
  }

  const summary = section.reportSectionSummary;
  const rating = mapScoreToNumber(summary?.score);
  const chassis = section.carChassisSubsection;

  // Map chassis items
  const chassisItems: InspectionItem[] = [
    {
      id: '1',
      name: 'Osłona silnika od spodu',
      status: mapEquipmentStatusToBadge(chassis?.bottomEngineCover),
      badgeCustomText: chassis?.bottomEngineCover,
      comments: chassis?.bottomEngineCoverRemark,
    },
    {
      id: '2',
      name: 'Wycieki silnika od spodu',
      status: mapChassisStatusToBadge(chassis?.bottomEngineLeaks),
      badgeCustomText: chassis?.bottomEngineLeaks,
      comments: chassis?.bottomEngineLeaksRemark,
    },
    {
      id: '3',
      name: 'Przekładnia kierownicza: wycieki i luzy',
      status: mapChassisStatusToBadge(chassis?.steeringGearLeaksAndPlay),
      badgeCustomText: chassis?.steeringGearLeaksAndPlay,
      comments: chassis?.steeringGearLeaksAndPlayRemark,
    },
    {
      id: '4',
      name: 'Stabilizatory oraz łączniki',
      status: mapChassisStatusToBadge(chassis?.stabilizersAndConnectors),
      badgeCustomText: chassis?.stabilizersAndConnectors,
      comments: chassis?.stabilizersAndConnectorsRemark,
    },
    {
      id: '5',
      name: 'Wahacze, tuleje wahaczy, sworznie',
      status: mapChassisStatusToBadge(chassis?.controlArmsBushingsPins),
      badgeCustomText: chassis?.controlArmsBushingsPins,
      comments: chassis?.controlArmsBushingsPinsRemark,
    },
    {
      id: '6',
      name: 'Końcówki drążków',
      status: mapChassisStatusToBadge(chassis?.tieRodEnds),
      badgeCustomText: chassis?.tieRodEnds,
      comments: chassis?.tieRodEndsRemark,
    },
    {
      id: '7',
      name: 'Ukł. napędowy: przeguby, półosie, wał napędowy',
      status: mapChassisStatusToBadge(chassis?.driveSystemJointsAxlesShaft),
      badgeCustomText: chassis?.driveSystemJointsAxlesShaft,
      comments: chassis?.driveSystemJointsAxlesShaftRemark,
    },
    {
      id: '8',
      name: 'Łożyska wszystkich kół',
      status: mapChassisStatusToBadge(chassis?.allWheelBearings),
      badgeCustomText: chassis?.allWheelBearings,
      comments: chassis?.allWheelBearingsRemark,
    },
    {
      id: '9',
      name: 'Stan opon od strony wewnętrznej',
      status: mapChassisStatusToBadge(chassis?.tireConditionInnerSide),
      badgeCustomText: chassis?.tireConditionInnerSide,
      comments: chassis?.tireConditionInnerSideRemark,
    },
    {
      id: '10',
      name: 'Wizualne oględziny amortyzatorów',
      status: mapChassisStatusToBadge(chassis?.shockAbsorbersVisualInspection),
      badgeCustomText: chassis?.shockAbsorbersVisualInspection,
      comments: chassis?.shockAbsorbersVisualInspectionRemark,
    },
    {
      id: '11',
      name: 'Ocena wizualna tarcz hamulcowych',
      status: mapChassisStatusToBadge(chassis?.brakeDiscsVisualAssessment),
      badgeCustomText: chassis?.brakeDiscsVisualAssessment,
      comments: chassis?.brakeDiscsVisualAssessmentRemark,
    },
    {
      id: '12',
      name: 'Ocena wizualna klocków hamulcowych',
      status: mapChassisStatusToBadge(chassis?.brakePadsVisualAssessment),
      badgeCustomText: chassis?.brakePadsVisualAssessment,
      comments: chassis?.brakePadsVisualAssessmentRemark,
    },
    {
      id: '13',
      name: 'Ocena przewodów hamulcowych',
      status: mapChassisStatusToBadge(chassis?.brakeLinesAssessment),
      badgeCustomText: chassis?.brakeLinesAssessment,
      comments: chassis?.brakeLinesAssessmentRemark,
    },
    {
      id: '14',
      name: 'Widoczne naprawy powypadkowe',
      status: mapChassisStatusToBadge(chassis?.visibleAccidentRepairs),
      badgeCustomText: chassis?.visibleAccidentRepairs,
      comments: chassis?.visibleAccidentRepairsRemark,
    },
    {
      id: '15',
      name: 'Widoczne elementy korozji',
      status: mapChassisStatusToBadge(chassis?.visibleCorrosionElements),
      badgeCustomText: chassis?.visibleCorrosionElements,
      comments: chassis?.visibleCorrosionElementsRemark,
    },
    {
      id: '16',
      name: 'Układ wydechowy',
      status: mapChassisStatusToBadge(chassis?.exhaustSystem),
      badgeCustomText: chassis?.exhaustSystem,
      comments: chassis?.exhaustSystemRemark,
    },
    {
      id: '17',
      name: 'Progi',
      status: mapChassisStatusToBadge(chassis?.sills),
      badgeCustomText: chassis?.sills,
      comments: chassis?.sillsRemark,
    },
  ];

  return {
    ratingSection: {
      title: 'Ocena podwozia',
      rating,
      ratingMessage: rating !== null ? getRatingMessage(rating) : 'Brak oceny',
      description: summary?.remarks && summary?.remarks?.length > 0 ? "Zwróć uwagę na podsumowanie wybranych zastrzeżeń dotyczących tej sekcji." : null,
      defectFree: summary?.defectFree ?? false,
    },
    remarks: summary?.remarks || [],
    chassisItems,
    expertSummary: chassis?.expertSummary || null,
  };
};
